<template>
  <div
    ref="target"
    class="menu-wrapper-user-container"
  >
    <div class="header-mobile">
      <dn-link
        class="header-mobile-logo"
        href="#"
      >
        <dn-logo
          class="dn-logo dn-logo-small"
          width="auto"
          name="logo"
        />
      </dn-link>
      <div class="header-mobile-button">
        <dn-button
          class="button-close"
          @click="$emit('hide-user-menu')"
        >
          <span />
          <span />
          <span />
        </dn-button>
      </div>
    </div>
    <div class="header-user">
      <div class="header-user-content">
        <dn-button
          v-if="!showDefaultUserAvatar"
          class="pill button-login-status"
          :text="loggedInUserUserAvatarName"
        />
        <dn-button
          v-else
          class="pill button-login-status"
          icon="user"
        />
        <div class="header-user-info">
          <label class="header-user-fullname">{{ loggedInUserFullName }}</label>
          <dn-link
            v-if="showUserSubscriptionStatus"
            :text="t(subscriptionType)"
            href="/min-side/"
            external
          />
        </div>
      </div>
      <dn-button
        class="button-close"
        @click="$emit('hide-user-menu')"
      >
        <span />
        <span />
        <span />
      </dn-button>
    </div>
    <dn-link
      v-if="shouldShowBliAbonnentLink && showUserSubscriptionStatus"
      class="outline full-width button-inverted-primary"
      text="Bli abonnent"
      href="/bli-abonnent/"
      external
    />
    <div class="user-menu-content">
      <template
        v-for="section in userMenu.sections"
        :key="section.name"
      >
        <div
          v-if="section.name === 'auth' && rtConfPub.showThemeSwitch"
          class="appearance-block"
        >
          <h3 class="appearance-title">
            {{ t('theme.title') }}
          </h3>
          <DnThemeSwitchOptions />
        </div>
        <ul>
          <li
            v-for="link in section.links"
            :key="link.name"
          >
            <dn-link
              v-if="section.name === 'auth'"
              :href="addTargetToUrl(link.url)"
              :text="link.name"
            />
            <client-only
              v-else-if="link.name === 'company-access'"
            >
              <dn-link
                v-if="profileLoginStore.companyLogin.access"
                :text="profileLoginStore.companyLogin.adminLinkText"
                :href="profileLoginStore.companyLogin.adminLink"
                :external="true"
              />
            </client-only>
            <dn-link
              v-else
              :href="link.url"
              :external="!!link.external"
              :text="link.name"
            />
          </li>
        </ul>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onClickOutside } from '@vueuse/core';
import { ref } from 'vue';
import { useProfileLoginStore } from '~/store/profileLogin';
import { type Sections } from '~/types';

const { t } = useI18n();
const emits = defineEmits(['hide-user-menu', 'toggle-user-main-menu']);
const target = ref(null);

const profileLoginStore = useProfileLoginStore();
const { showDefaultUserAvatar, loggedInUserUserAvatarName, subscriptionType } = useProfileLogin(profileLoginStore.loginStatus);
const rtConfPub = useRuntimeConfig().public;
const showUserSubscriptionStatus = rtConfPub.showUserSubscriptionStatus;
const userMenu = ref<Sections>(rtConfPub.config.userMenuConfig.sections[0]);
const loggedInUserFullName = computed(() => {
  const fullName = (profileLoginStore.loginStatus.firstName || '') + ' ' + (profileLoginStore.loginStatus.lastName || '');
  if (!showDefaultUserAvatar.value) {
    return fullName;
  } else {
    return profileLoginStore.loginStatus.username;
  }
});

const shouldShowBliAbonnentLink = computed(() => {
  return profileLoginStore.loginStatus.isLoggedIn && !profileLoginStore.loginStatus.isSubscriber;
});

onClickOutside(target, () => emits('hide-user-menu'));

const addTargetToUrl = (path) => {
  const urlHref = useRequestURL().href;
  const origin = useRequestURL().origin;
  const url = new URL(path, origin);
  url.searchParams.append('target', urlHref);
  return url.toString();
};
</script>

<style lang="scss">
.user-menu-open {
  &::before {
    --_dn-header_overlay_background-color: var(--dn-header_overlay_background-color, rgb(0 0 0 / 50%));

    content: '';
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: var(--_dn-header_overlay_background-color);
    z-index: 5001;
  }
}

.menu-wrapper-user {
  position: fixed;
  right: -630px;
  top: 0;
  z-index: 5002;
  background: var(--color-usermenu-bg);
  width: 100%;
  height: 100vh;
  font-size: 16px;
  color: var(--color-typography-inverted);
  line-height: 24px;
  opacity: 0;
  transition: all 0.3s linear;

  @media only screen and (width >= 640px) {
    width: 518px;
  }

  .user-menu-open & {
    right: 0;
    opacity: 1;
  }

  &-container {
    height: 100%;
    padding: 16px;

    --color-header-logo-primary: var(--color-usermenu-fg);
    --color-header-logo-secondary: var(--color-usermenu-fg);
    --color-header-logo-tertiary: var(--color-usermenu-fg);
    --dn-theme-options_background: var(--color-brand-utility-600);
    --dn-theme-options_border-color: var(--color-brand-utility-400);
    --dn-theme-options_font-family: var(--typography-utility-l-font-family);
    --dn-theme-options_font-size: var(--typography-utility-l-font-size);
    --dn-theme-options_line-height: var(--typography-utility-l-line-height);
    --dn-theme-options_color: var(--color-usermenu-fg);

    @media only screen and (width >= 768px) {
      padding: 48px 32px;
    }
  }

  .dn-button {
    flex: 1;
    border: 1px solid transparent;
    font-size: 12px;
    line-height: 12px;
    text-align: center;
    padding: 13px 26px;

    @media only screen and (width >= 640px) {
      font-size: 14px;
      line-height: 14px;
      padding: 16px 32px;
    }

    + .button {
      margin-left: 16px;

      @media only screen and (width >= 640px) {
        margin-left: 40px;
      }
    }

    &-border {
      border-color: rgba(255 255 255 / 20%);
    }

    &.button-close {
      position: relative;
      display: flex;
      min-width: 32px;
      min-height: 28px;
      padding: 0;
      border: none;

      span {
        position: absolute;
        left: 0;
        background-color: var(--color-usermenu-fg);
        width: 100%;
        height: 1px;
        transition: all 0.2s linear;
        transform: rotate(0deg) scaleY(1.5);
        transform-origin: top;

        &:nth-child(1) {
          left: 50%;
          top: 18px;
          width: 0;
        }

        &:nth-child(2),
        &:nth-child(3) {
          top: 16px;
        }

        &:nth-child(2) {
          transform: rotate(45deg) scaleY(1.5);
        }

        &:nth-child(3) {
          transform: rotate(-45deg) scaleY(1.5);
        }
      }

      &:hover {
        --dn-button_background_hover: none;

        border: none;
      }

      &:active {
        --dn-button_background_active: none;
      }
    }
  }

  ul {
    padding: 0;
    margin: 0;

    li {
      a {
        --dn-link_display: block;
        --dn-link_font-family: var(--typography-utility-l-font-family);
        --dn-link_font-size: var(--typography-utility-l-font-size);
        --dn-link_line-height: var(--typography-utility-l-line-height);
        --dn-link_color: var(--color-usermenu-fg);
        --dn-link_color__hover: var(--color-usermenu-fg);
        --dn-link_color__active: var(--color-usermenu-fg);

        text-decoration: none;
        padding: 16px 0;

        @media only screen and (width >= 768px) {
          padding: 16px;
        }

        &:hover {
          font-family: var(--typography-editorial-subhead-max-font-family);
        }
      }
    }

    &.dn-theme-options {
      --dn-icon_color: var(--color-usermenu-fg);

      border: none;
      padding: 0 16px;
    }
  }

  .header {
    &-mobile {
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 1px solid rgb(255 255 255 / 30%);
      padding-bottom: 16px;
      margin-bottom: 24px;

      @media only screen and (width >= 768px) {
        display: none;
      }

      &-logo {
        display: flex;

        .dn-logo-small {
          height: 24px;
        }
      }

      &-button {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
      }
    }

    &-user {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 32px;

      button {
        &.button {
          &-login-status {
            --dn-login-status-border_color: var(--color-usermenu-fg);
            --dn-login-status-icon_color: var(--color-brand-utility-900);
            --dn-header-button_background: var(--color-usermenu-fg);
            --dn-header-button_color: var(--color-brand-utility-900);

            flex: 0 0 48px;
            min-width: 48px;
            height: 48px;
            border-radius: 24px;
            font-size: 14px;

            .icon {
              flex: 1 0 36px;
              max-width: 36px;
              height: 36px;
              margin-right: 0;
            }
          }

          &-close {
            @media only screen and (width < 768px) {
              display: none;
            }
          }
        }
      }

      &-content {
        display: flex;
        align-items: center;
        gap: 16px;
        width: 100%;
      }

      &-fullname {
        --dn_font-family: var(--typography-utility-l-bold-font-family);
        --dn_font-size: var(--typography-utility-l-bold-font-size);
        --dn_font-weight: var(--typography-utility-l-bold-font-weight);
        --dn_line-height: var(--typography-utility-l-bold-line-height);
        --dn_color: var(--color-usermenu-fg);
      }

      &-info {
        display: flex;
        flex-direction: column;
        line-height: 1;
        gap: 4px;

        .dn-link {
          --dn-link_font-family: var(--typography-utility-m-font-family);
          --dn-link_font-size: var(--typography-utility-m-font-size);
          --dn-link_color: var(--color-usermenu-subtle-fg);
          --dn-link_color__hover: var(--color-usermenu-subtle-fg);
          --dn-link_color__active: var(--color-usermenu-subtle-fg);

          min-height: auto;
          padding: 0;
        }
      }
    }
  }
}

.appearance-block {
  padding: 0 0 24px;

  @media only screen and (width >= 768px) {
    padding: 24px 0;
  }
}

.appearance-title {
  font-family: var(--typography-utility-s-font-family);
  font-size: 14px;
  color: var(--color-teaser-brand-subtle-fg);
  font-weight: var(--typography-utility-s-font-weight);
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 14px 0;

  @media only screen and (width >= 768px) {
    padding: 14px 16px;
  }
}

.login {
  transition: all 0.2s linear;

  &:hover {
    background-color: #1d3666;
  }

  &:active {
    background-color: #060f1f;
  }
}

.user-menu-content {
  height: calc(100vh - 274px);
  border-top: 1px solid var(--color-usermenu-divider);
  overflow-y: auto;
  padding-top: 8px;
  margin-top: 16px;

  @media only screen and (width >= 768px) {
    height: calc(100vh - 224px);
  }
}
</style>
